import { useEffect, useRef } from "react";
import { useChatbot } from "@/hooks/useChatbot";
import { ChatHeader } from "./ChatHeader";
import { SupportHub } from "./SupportHub";
import { ChatBubble } from "./ChatBubble";
import { ChatInput } from "./ChatInput";
import { cn } from "@/lib/utils";

interface ChatWidgetProps {
  className?: string;
}

export function ChatWidget({ className }: ChatWidgetProps) {
  const {
    messages,
    isLoading,
    isOpen,
    setIsOpen,
    sendMessage,
    clearChat,
    sessionRemaining,
    dailyRemaining,
    error,
  } = useChatbot();

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current && messagesContainerRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isLoading]);

  const isBlocked = sessionRemaining <= 0 || dailyRemaining <= 0;

  if (!isOpen) {
    return <SupportHub onOpenChat={() => setIsOpen(true)} isChatOpen={false} />;
  }

  return (
    <div
      className={cn(
        "fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-48px)] h-[600px] max-h-[calc(100vh-100px)]",
        "flex flex-col bg-background border rounded-2xl shadow-2xl overflow-hidden",
        "animate-in fade-in slide-in-from-bottom-4 duration-300",
        className
      )}
    >
      <ChatHeader
        onClose={() => setIsOpen(false)}
        onClear={clearChat}
        sessionRemaining={sessionRemaining}
      />

      <div
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto p-4 space-y-3"
      >
        {messages.map((msg) => (
          <ChatBubble
            key={msg.id}
            role={msg.role}
            content={msg.content}
          />
        ))}
        
        {isLoading && messages[messages.length - 1]?.role === "user" && (
          <ChatBubble role="assistant" content="" isLoading />
        )}

        {isBlocked && (
          <div className="text-center py-4 text-sm text-muted-foreground">
            <p className="font-medium text-destructive">
              {sessionRemaining <= 0 
                ? "Limite desta conversa atingido"
                : "Limite diário atingido"}
            </p>
            <p className="mt-1">
              {sessionRemaining <= 0
                ? "Clique em ↻ para iniciar nova conversa"
                : "O limite renova à meia-noite"}
            </p>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <ChatInput
        onSend={sendMessage}
        disabled={isLoading || isBlocked}
        placeholder={
          isBlocked
            ? "Limite atingido"
            : "Pergunte sobre o ProntuÁudio..."
        }
      />

      {/* Quick actions for empty state */}
      {messages.length <= 1 && !isLoading && (
        <div className="px-4 pb-3 flex flex-wrap gap-2">
          {[
            "Como funciona?",
            "Quais os planos?",
            "Como gravar?",
          ].map((suggestion) => (
            <button
              key={suggestion}
              onClick={() => sendMessage(suggestion)}
              className="text-xs px-3 py-1.5 rounded-full bg-muted hover:bg-muted/80 text-muted-foreground transition-colors"
            >
              {suggestion}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
